import React from "react";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import EventIcon from "@mui/icons-material/Event";
import ArticleIcon from "@mui/icons-material/Article";
// material
import {
  Avatar,
  Box,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { Label } from "src/components";
import { fDateShort } from "src/utils/formatTime";
import DateRangeRoundedIcon from "@mui/icons-material/DateRangeRounded";
import { Link as RouterLink } from "react-router-dom";
import { ApiUrl } from "src/services/apiUrls";

const RootStyle = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  boxShadow: theme.customShadows?.z8 || "0 8px 20px rgba(0,0,0,0.08)",
}));

const ThumbStyle = styled("img")(({ theme }) => ({
  width: 46,
  height: 46,
  flexShrink: 0,
  objectFit: "cover",
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
}));

const getSafeDate = (date) => {
  const parsedDate = new Date(date);
  return date && !Number.isNaN(parsedDate.getTime())
    ? fDateShort(parsedDate)
    : "-";
};

const getImageUrl = (image) => {
  if (!image) return "";
  if (/^https?:\/\//i.test(image)) return image;
  return `${ApiUrl.IMAGE_BASE_URL}${image}`;
};

export default function BlogCard({
  item,
  row,
  isLoading,
  serialNumber,
  editBasePath = "/blog/blogs_events",
}) {
  const theme = useTheme();
  const data = item || row || {};
  const itemId = data?._id || data?.id;
  const rawStatus = data?.status;
  const normalizedStatus = String(rawStatus ?? "").toLowerCase();
  const isActive =
    normalizedStatus === "active" ||
    normalizedStatus === "true" ||
    rawStatus === true ||
    data?.is_active === true ||
    data?.is_active === 1;
  const isEvent = data?.is_event === true || data?.is_event === "true";
  const editPath = itemId ? `${editBasePath}/${itemId}` : editBasePath;
  const title = data?.name || data?.title || "Untitled";
  const imageUrl = getImageUrl(data?.image);

  return (
    <RootStyle
      sx={{
        borderLeft: `6px solid ${
          isLoading
            ? theme.palette.divider
            : theme.palette[isActive ? "success" : "error"].main
        }`,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        {isLoading ? (
          <Skeleton variant="rounded" width={46} height={46} />
        ) : imageUrl ? (
          <ThumbStyle alt={title} src={imageUrl} />
        ) : (
          <Avatar
            variant="rounded"
            sx={{ width: 46, height: 46, flexShrink: 0 }}
          >
            {isEvent ? <EventIcon fontSize="small" /> : <ArticleIcon fontSize="small" />}
          </Avatar>
        )}

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {isLoading ? <Skeleton width={32} /> : `#${serialNumber}`}
          </Typography>
          <Typography
            component={RouterLink}
            to={editPath}
            variant="subtitle1"
            fontWeight={700}
            noWrap
            sx={{
              display: "block",
              color: "text.primary",
              textDecoration: "none",
            }}
          >
            {isLoading ? <Skeleton width="80%" /> : title}
          </Typography>

          <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
            {isLoading ? (
              <Skeleton width={70} height={24} />
            ) : (
              <>
                <Label
                  variant={theme.palette.mode === "light" ? "ghost" : "filled"}
                  color={isActive ? "success" : "error"}
                >
                  {isActive ? "Active" : "in-active"}
                </Label>
                <Label
                  variant={theme.palette.mode === "light" ? "ghost" : "filled"}
                  color={isEvent ? "info" : "default"}
                >
                  {isEvent ? "Event" : "Blog"}
                </Label>
              </>
            )}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1 }}>
            {isLoading ? (
              <Skeleton variant="circular" width={18} height={18} />
            ) : (
              <DateRangeRoundedIcon fontSize="small" />
            )}
            <Typography variant="caption" color="text.secondary">
              {isLoading ? (
                <Skeleton width={72} />
              ) : (
                getSafeDate(data?.createdAt || data?.created_at)
              )}
            </Typography>
          </Stack>
        </Box>

        {isLoading ? (
          <Skeleton variant="circular" width={34} height={34} />
        ) : (
          <Tooltip title="Edit">
            <IconButton
              size="small"
              component={RouterLink}
              to={editPath}
              disabled={!itemId}
              sx={{ flexShrink: 0 }}
            >
              <EditRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </RootStyle>
  );
}
